import PropTypes from "prop-types";
import { useEffect, useId, useRef, useState } from "react";
import {
  Alert,
  Box,
  ButtonBase,
  Card,
  Checkbox,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { IconDotsVertical, IconInbox, IconRefresh, IconSearch, IconX } from "@tabler/icons";
import ActionButton, { iconButtonSx } from "./ActionButton";
import { useTopBarHeight } from "src/pages/cases/workspace";

// The CMS list, on the Cases model: rows people read, not a database dump.
//
//  - The first column is the item: avatar or icon, title, one quiet line.
//    The whole row opens it (`onOpen`); the title is the keyboard target.
//  - Only human columns; each can hide below a breakpoint (`hideBelow`).
//  - No checkbox on every row: a "Select" button appears when there are
//    `bulkActions`, and turns selection on.
//  - A few items show as cards (`sparse`), and on a phone always do; the
//    table header sticks under the top bar.
//  - Loading, empty, error and "nothing matches" are designed states.
//
//   <ResourceList
//     label="Staff"
//     items={rows} getKey={(r) => r.id}
//     primary={(r) => ({ title: r.fullname, subtitle: r.email, avatar: <PersonAvatar name={r.fullname} src={r.main_img} /> })}
//     columns={[{ key: "role", label: "Role", render: (r) => <Chip label={humanize(r.typeName)} /> }]}
//     onOpen={(r) => edit(r)} openLabel={(r) => `Edit ${r.fullname}`}
//     openHref={(r) => `/cases/${r.id}`}   (optional: the title becomes a real link, for a new tab)
//     rowActions={(r) => [{ label: "Edit", onClick: () => edit(r) }, { label: "Delete", destructive: true, onClick: () => remove(r) }]}
//       (an action can be { disabled: true, hint: "Why not" }: shown greyed with its reason)
//     bulkActions={[{ label: "Delete", destructive: true, onClick: (rows) => removeMany(rows) }]}
//     search={{ value, onChange, placeholder: "Search by name or email" }}
//     filters={<TextField select size="small" value={role} onChange={…} SelectProps={{ SelectDisplayProps: { "aria-label": "Role" } }}>…</TextField>}
//       (name a select through SelectDisplayProps, the visible combobox; inputProps only names the hidden native input)
//       (on a phone they fold behind a "Filters" button; pass filtersActive to say "Filters · on" while one is set)
//     loading={loading} error={failed} onRetry={load}
//     empty={{ title: "No staff yet.", hint: "Add the first person.", action: <ActionButton …/> }}
//   />

// The ?q= of the address, if any.
export function searchFromUrl(search = typeof window !== "undefined" ? window.location.search : "") {
  try {
    return (new URLSearchParams(search).get("q") || "").trim().slice(0, 120);
  } catch {
    return "";
  }
}

const isInteractive = (target, row) => {
  let el = target;
  while (el && el !== row) {
    if (/^(BUTTON|A|INPUT|LABEL|SELECT|TEXTAREA)$/.test(el.tagName) || el.getAttribute?.("role") === "switch" || el.getAttribute?.("role") === "menuitem") return true;
    el = el.parentElement;
  }
  return false;
};

function RowMenu({ actions, label }) {
  const [anchor, setAnchor] = useState(null);
  const list = (actions || []).filter(Boolean);
  if (!list.length) return null;
  const safe = list.filter((a) => !a.destructive);
  const risky = list.filter((a) => a.destructive);
  // A `disabled` action stays in the menu (still reachable by keyboard)
  // with its `hint` saying why, rather than vanishing.
  const item = (a) => (
    <MenuItem
      key={a.label}
      disabled={!!a.disabled}
      onClick={() => {
        setAnchor(null);
        a.onClick?.();
      }}
      sx={[
        a.destructive && { color: (t) => (t.palette.mode === "dark" ? t.palette.secondary.main : t.palette.secondary.dark) },
        !!a.hint && { flexDirection: "column", alignItems: "flex-start", whiteSpace: "normal", maxWidth: 280 },
        // Greyed label, but a readable reason.
        !!a.disabled && { "&.Mui-disabled": { opacity: 1, color: "text.disabled" } },
      ]}
    >
      {a.icon ? <ListItemIcon sx={{ color: "inherit" }}>{a.icon}</ListItemIcon> : null}
      {a.label}
      {a.hint ? (
        <Typography component="span" variant="body2" color="text.secondary">
          {a.hint}
        </Typography>
      ) : null}
    </MenuItem>
  );
  return (
    <>
      <Tooltip title="Actions">
        <IconButton
          aria-label={`Actions for ${label}`}
          aria-haspopup="true"
          aria-expanded={anchor ? "true" : undefined}
          onClick={(e) => {
            e.stopPropagation();
            setAnchor(e.currentTarget);
          }}
          sx={iconButtonSx}
        >
          <IconDotsVertical size={18} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 190 } }}
        MenuListProps={{ disabledItemsFocusable: true }}
        // Opened inside <main> (not at the end of <body>), so the menu stays in
        // the page's main landmark.
        container={() => document.querySelector("main") || document.body}
      >
        {safe.map(item)}
        {safe.length > 0 && risky.length > 0 && <Divider sx={{ my: 0.5 }} />}
        {risky.map(item)}
      </Menu>
    </>
  );
}

RowMenu.propTypes = { actions: PropTypes.array, label: PropTypes.string };

// The item itself: avatar, title (the keyboard target that opens it), and a
// quiet line under it.
function Primary({ item, primary, onOpen, openLabel, openHref, dense }) {
  // With an address the title is a real link: Ctrl/⌘-click or a middle
  // click opens it in a new tab; a plain click opens it here, as before.
  const href = openHref ? openHref(item) : null;
  const p = primary(item) || {};
  const title = (
    <Typography component="span" sx={{ fontWeight: 600, display: "block", overflowWrap: "anywhere", lineHeight: 1.35 }}>
      {p.title}
    </Typography>
  );
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
      {p.avatar || null}
      <Box sx={{ minWidth: 0 }}>
        {onOpen ? (
          <ButtonBase
            {...(href ? { component: "a", href } : {})}
            onClick={(e) => {
              e.stopPropagation();
              if (href && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) return;
              e.preventDefault();
              onOpen(item);
            }}
            aria-label={openLabel ? openLabel(item) : undefined}
            sx={{
              textAlign: "left",
              borderRadius: 1,
              display: "block",
              color: "text.primary",
              "&:hover .rl-title, &:focus-visible .rl-title": { color: "primary.main", textDecoration: "underline" },
              "&.Mui-focusVisible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
            }}
          >
            <Box component="span" className="rl-title">
              {title}
            </Box>
          </ButtonBase>
        ) : (
          title
        )}
        {p.subtitle ? (
          <Typography component="div" variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere", mt: dense ? 0 : 0.25 }}>
            {p.subtitle}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

Primary.propTypes = {
  item: PropTypes.any,
  primary: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  openLabel: PropTypes.func,
  openHref: PropTypes.func,
  dense: PropTypes.bool,
};

const ResourceList = ({
  label,
  items = [],
  getKey = (item) => item.id,
  primary,
  primaryLabel = "Name",
  columns = [],
  onOpen,
  openLabel,
  openHref,
  rowActions,
  bulkActions,
  search,
  filters,
  loading = false,
  error,
  onRetry,
  empty = {},
  pagination,
  sparse = 3,
  cards,
  filtersActive = false,
}) => {
  const phone = useMediaQuery((t) => t.breakpoints.down("md"));
  const phoneWidth = useMediaQuery((t) => t.breakpoints.down("sm"));
  const lgDown = useMediaQuery((t) => t.breakpoints.down("lg"));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersId = useId();
  const topBar = useTopBarHeight();
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState([]);
  const rows = Array.isArray(items) ? items : [];
  const keys = rows.map(getKey);
  const keyString = keys.join("\u0001");

  // A link can open the list already searched: /documents?q=Passport (the
  // Ctrl K search links here). Read once, when the list first shows.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    const asked = searchFromUrl();
    if (asked && search?.onChange && !String(search.value ?? "").trim()) search.onChange(asked);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Selection only keeps what is still on screen (and changes nothing when
  // nothing left the screen, so it never loops).
  useEffect(() => {
    const onScreen = new Set(keyString.split("\u0001"));
    setSelected((s) => {
      const kept = s.filter((k) => onScreen.has(String(k)));
      return kept.length === s.length ? s : kept;
    });
  }, [keyString]);

  const visibleColumns = columns.filter(
    (c) => !(c.hideBelow === "lg" && lgDown) && !(c.hideBelow === "md" && phone),
  );
  const hasActions = !!rowActions;
  const searching = !!(search?.value && String(search.value).trim());
  const asCards = phone || cards === true || (cards !== false && !loading && rows.length > 0 && rows.length <= sparse && !pagination?.total);
  const selectedItems = rows.filter((r) => selected.includes(getKey(r)));
  const toggle = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  const nameOf = (item) => {
    const t = primary(item)?.title;
    return typeof t === "string" ? t : openLabel ? openLabel(item) : "this item";
  };

  const openRow = (e, item) => {
    if (!onOpen || isInteractive(e.target, e.currentTarget)) return;
    if (selecting) {
      toggle(getKey(item));
      return;
    }
    onOpen(item);
  };

  // ---- toolbar: search and filters on the left, Select on the right ----
  // On a phone the filters fold behind one "Filters" button beside the
  // search, so the list starts near the top of the screen.
  const foldFilters = phoneWidth && !!filters;
  const toolbar =
    search || filters || bulkActions ? (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, "&:empty": { display: "none" } }}>
          {search && (
            <TextField
              size="small"
              value={search.value ?? ""}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder || "Search"}
              sx={{ width: { xs: "100%", sm: 320 }, flex: { xs: 1, sm: "none" } }}
              inputProps={{ "aria-label": search.label || search.placeholder || `Search ${label || "this list"}` }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconSearch size={18} />
                  </InputAdornment>
                ),
                endAdornment: searching ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Clear the search" onClick={() => search.onChange("")} sx={iconButtonSx}>
                      <IconX size={16} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          )}
          {foldFilters && (
            <ActionButton
              variant="outlined"
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
              aria-controls={filtersId}
              sx={{ flexShrink: 0, minHeight: 44 }}
            >
              {filtersOpen ? "Hide filters" : filtersActive ? "Filters · on" : "Filters"}
            </ActionButton>
          )}
          </Stack>
          {foldFilters ? (
            <Box id={filtersId} hidden={!filtersOpen} sx={{ "& > *": { width: "100%" } }}>
              <Stack spacing={1.5}>{filters}</Stack>
            </Box>
          ) : (
            filters
          )}
        </Stack>
        {bulkActions && rows.length > 0 && !selecting && (
          <ActionButton variant="text" onClick={() => setSelecting(true)} sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}>
            Select
          </ActionButton>
        )}
      </Stack>
    ) : null;

  // ---- selection bar ----
  const selectionBar = selecting ? (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      role="region"
      aria-label="Selection"
      sx={{ mb: 1.5, px: 1.5, py: 0.75, borderRadius: 999, bgcolor: "primary.light" }}
    >
      <Checkbox
        size="small"
        checked={rows.length > 0 && selected.length === rows.length}
        indeterminate={selected.length > 0 && selected.length < rows.length}
        onChange={(e) => setSelected(e.target.checked ? keys : [])}
        inputProps={{ "aria-label": "Select every row shown" }}
      />
      <Typography fontWeight={600} aria-live="polite" sx={{ mr: "auto" }}>
        {selected.length ? `${selected.length} selected` : "Choose rows"}
      </Typography>
      {(bulkActions || []).map((a) => (
        <ActionButton
          key={a.label}
          variant="outlined"
          tone={a.destructive ? "destructive" : undefined}
          disabled={!selected.length}
          onClick={() => a.onClick(selectedItems, () => setSelected([]))}
        >
          {a.label}
          {selected.length ? ` ${selected.length}` : ""}
        </ActionButton>
      ))}
      <ActionButton
        variant="text"
        onClick={() => {
          setSelecting(false);
          setSelected([]);
        }}
      >
        Done
      </ActionButton>
    </Stack>
  ) : null;

  // ---- states ----
  const failed = !!error && !loading;
  const errorBox = failed ? (
    <Alert
      severity="error"
      sx={{ mb: 1.5, alignItems: "center" }}
      action={
        onRetry ? (
          <ActionButton onClick={onRetry} startIcon={<IconRefresh size={16} />}>
            Try again
          </ActionButton>
        ) : undefined
      }
    >
      {typeof error === "string" ? error : "This list did not load. Check your connection and try again."}
    </Alert>
  ) : null;

  const emptyBox =
    !loading && !failed && rows.length === 0 ? (
      <Card variant="outlined" sx={{ p: { xs: 3, sm: 5 } }}>
        <Stack alignItems="center" spacing={1} textAlign="center" role="status">
          <Box sx={{ color: "text.secondary" }} aria-hidden>
            {empty.icon || <IconInbox size={32} stroke={1.5} />}
          </Box>
          <Typography fontWeight={600}>{searching ? `Nothing matches “${String(search.value).trim()}”.` : empty.title || "Nothing here yet."}</Typography>
          {searching ? (
            <ActionButton onClick={() => search.onChange("")}>Clear the search</ActionButton>
          ) : (
            <>
              {empty.hint && (
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
                  {empty.hint}
                </Typography>
              )}
              {empty.action || null}
            </>
          )}
        </Stack>
      </Card>
    ) : null;

  const skeleton = loading && rows.length === 0 && (
    <Card variant="outlined" sx={{ p: 1.5 }} aria-busy="true" aria-label={`Loading ${label || "the list"}`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Stack key={i} direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.25, px: 1 }}>
          <Skeleton variant="circular" width={36} height={36} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="40%" />
            <Skeleton width="25%" />
          </Box>
          <Skeleton width={80} sx={{ display: { xs: "none", sm: "block" } }} />
        </Stack>
      ))}
    </Card>
  );

  // ---- cards (few items, or a phone) ----
  const cardView = (
    <Box
      component="ul"
      aria-label={label}
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        display: "grid",
        gap: 1.5,
        // A few items read best as cards side by side; a phone stacks them.
        gridTemplateColumns: phone ? "1fr" : "repeat(auto-fill, minmax(360px, 1fr))",
      }}
    >
      {rows.map((item) => {
        const key = getKey(item);
        const isSel = selected.includes(key);
        return (
          <Card
            key={key}
            component="li"
            variant="outlined"
            onClick={(e) => openRow(e, item)}
            sx={{
              p: { xs: 1.75, sm: 2 },
              cursor: onOpen || selecting ? "pointer" : "default",
              transition: "border-color 120ms, background-color 120ms",
              "&:hover": onOpen ? { borderColor: "primary.main" } : undefined,
              ...(isSel && { bgcolor: "primary.light", borderColor: "primary.main" }),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              {selecting && (
                <Checkbox
                  checked={isSel}
                  onChange={() => toggle(key)}
                  inputProps={{ "aria-label": `Select ${nameOf(item)}` }}
                  sx={{ mt: -0.5, ml: -1 }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Primary item={item} primary={primary} onOpen={selecting ? undefined : onOpen} openLabel={openLabel} openHref={openHref} />
                {columns.some((c) => c.inCard !== false && !c.cardAside) && (
                  <Box
                    component="dl"
                    sx={{
                      m: 0,
                      mt: 1.25,
                      display: "grid",
                      gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(auto-fill, minmax(160px, 1fr))" },
                      columnGap: 2,
                      rowGap: 1,
                    }}
                  >
                    {columns
                      .filter((c) => c.inCard !== false && !c.cardAside)
                      .map((c) => (
                        <Box key={c.key} sx={{ minWidth: 0 }}>
                          <Typography component="dt" variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600 }}>
                            {c.label}
                          </Typography>
                          <Box component="dd" sx={{ m: 0, fontSize: "0.875rem", overflowWrap: "anywhere" }}>
                            {c.render(item)}
                          </Box>
                        </Box>
                      ))}
                  </Box>
                )}
              </Box>
              {/* Short controls (a switch) sit at the card's edge, not in a block of their own. */}
              {columns
                .filter((c) => c.cardAside)
                .map((c) => (
                  <Box key={c.key} sx={{ flexShrink: 0, mt: -0.5 }}>
                    {c.render(item)}
                  </Box>
                ))}
              {hasActions && !selecting && <RowMenu actions={rowActions(item)} label={nameOf(item)} />}
            </Stack>
          </Card>
        );
      })}
    </Box>
  );

  // ---- table ----
  const tableView = (
    <Card variant="outlined" sx={{ p: 0, overflow: "visible" }}>
      <Table aria-label={label} sx={{ "& td, & th": { borderColor: "divider" } }}>
        <TableHead>
          <TableRow
            sx={{
              "& > th": {
                position: "sticky",
                top: topBar,
                zIndex: 2,
                bgcolor: "background.paper",
                py: 1.25,
                boxShadow: (t) => `inset 0 -1px 0 ${t.palette.divider}`,
                borderBottom: 0,
              },
              "& > th:first-of-type": { borderTopLeftRadius: 20 },
              "& > th:last-of-type": { borderTopRightRadius: 20 },
            }}
          >
            {selecting && (
              <TableCell padding="checkbox">
                <Box component="span" sx={visuallyHidden}>
                  Select
                </Box>
              </TableCell>
            )}
            <TableCell>{primaryLabel}</TableCell>
            {visibleColumns.map((c) => (
              <TableCell key={c.key} align={c.align || "left"} sx={{ width: c.width }}>
                {c.label}
              </TableCell>
            ))}
            {hasActions && (
              <TableCell align="right" sx={{ width: 56 }}>
                <Box component="span" sx={visuallyHidden}>
                  Actions
                </Box>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((item) => {
            const key = getKey(item);
            const isSel = selected.includes(key);
            return (
              <TableRow
                key={key}
                hover
                selected={isSel}
                onClick={(e) => openRow(e, item)}
                sx={{
                  cursor: onOpen || selecting ? "pointer" : "default",
                  "& > td": { py: 1.5, verticalAlign: "middle" },
                  "&:last-child > td": { borderBottom: 0 },
                }}
              >
                {selecting && (
                  <TableCell padding="checkbox">
                    <Checkbox checked={isSel} onChange={() => toggle(key)} inputProps={{ "aria-label": `Select ${nameOf(item)}` }} />
                  </TableCell>
                )}
                <TableCell sx={{ minWidth: 220 }}>
                  <Primary item={item} primary={primary} onOpen={selecting ? undefined : onOpen} openLabel={openLabel} openHref={openHref} />
                </TableCell>
                {visibleColumns.map((c) => (
                  <TableCell key={c.key} align={c.align || "left"} sx={{ width: c.width, color: "text.primary", fontSize: "0.875rem" }}>
                    {c.render(item)}
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell align="right" sx={{ pr: 1 }}>
                    {!selecting && <RowMenu actions={rowActions(item)} label={nameOf(item)} />}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );

  return (
    <Box>
      {toolbar}
      {selectionBar}
      {errorBox}
      {skeleton}
      {emptyBox}
      {rows.length > 0 && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 120ms" }} aria-busy={loading || undefined}>
          {asCards ? cardView : tableView}
        </Box>
      )}
      {pagination && rows.length > 0 && pagination.total > rows.length && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={Math.min(pagination.page, Math.max(0, Math.ceil(pagination.total / pagination.rowsPerPage) - 1))}
          rowsPerPage={pagination.rowsPerPage}
          rowsPerPageOptions={pagination.options || [10, 25, 50, 100]}
          labelRowsPerPage="Rows per page"
          onPageChange={(_, page) => pagination.onPageChange(page)}
          onRowsPerPageChange={(e) => pagination.onRowsPerPageChange?.(parseInt(e.target.value, 10))}
        />
      )}
    </Box>
  );
};

ResourceList.propTypes = {
  label: PropTypes.string,
  items: PropTypes.array,
  getKey: PropTypes.func,
  primary: PropTypes.func.isRequired,
  primaryLabel: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      render: PropTypes.func.isRequired,
      align: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      hideBelow: PropTypes.oneOf(["md", "lg"]),
      inCard: PropTypes.bool,
      cardAside: PropTypes.bool,
    }),
  ),
  onOpen: PropTypes.func,
  openLabel: PropTypes.func,
  openHref: PropTypes.func,
  rowActions: PropTypes.func,
  bulkActions: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, onClick: PropTypes.func, destructive: PropTypes.bool })),
  search: PropTypes.shape({ value: PropTypes.string, onChange: PropTypes.func, placeholder: PropTypes.string, label: PropTypes.string }),
  filters: PropTypes.node,
  filtersActive: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onRetry: PropTypes.func,
  empty: PropTypes.shape({ title: PropTypes.node, hint: PropTypes.node, action: PropTypes.node, icon: PropTypes.node }),
  pagination: PropTypes.shape({
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    total: PropTypes.number,
    onPageChange: PropTypes.func,
    onRowsPerPageChange: PropTypes.func,
    options: PropTypes.array,
  }),
  sparse: PropTypes.number,
  cards: PropTypes.bool,
};

export default ResourceList;
